import moment from 'moment';

import {getISODate} from 'utils/time';

const nextWeek = moment().add(1, 'w');
const lastWeek = moment().subtract(1, 'w');
const startCurrentMonth = moment().startOf('M');
const lastMonth = moment(startCurrentMonth).subtract(1, 'M');
const endLastMonth = moment(startCurrentMonth).subtract(1, 'd');
const lastTwoMonths = moment(startCurrentMonth).subtract(2, 'M');
const lastThreeMonths = moment(startCurrentMonth).subtract(3, 'M');
const lastSixMonths = moment(startCurrentMonth).subtract(6, 'M');
const lastYear = moment(startCurrentMonth).subtract(1, 'y');
const nextWeekEnd = moment().add(7, 'days');
const nextMonthEnd = moment().add(1, 'month');
const nextTwoMonthsEnd = moment().add(2, 'months');
const nextThreeMonthsEnd = moment().add(3, 'months');
const nextSixMonthsEnd = moment().add(6, 'months');
const nextYearEnd = moment().add(1, 'year');

const startOfWeek = (date) => moment(date).startOf('isoWeek').toDate();
const endOfWeek = (date) => moment(date).endOf('isoWeek').toDate();
const isBetweenWeek = (day, date) =>
  moment(day).isBetween(moment(date).startOf('isoWeek'), moment(date).endOf('isoWeek'));

const periodOpts = [
  {
    period: 'Next week',
    from: startOfWeek(nextWeek),
    to: endOfWeek(nextWeek)
  },
  {
    period: 'Current week',
    from: startOfWeek(moment()),
    to: endOfWeek(moment())
  },
  {
    period: 'Last week',
    from: startOfWeek(lastWeek),
    to: endOfWeek(lastWeek)
  },
  {
    period: 'Last month',
    from: startOfWeek(lastMonth),
    to: endOfWeek(endLastMonth)
  },
  {
    period: 'Last 2 months',
    from: startOfWeek(lastTwoMonths),
    to: endOfWeek(endLastMonth)
  },
  {
    period: 'Last 3 months',
    from: startOfWeek(lastThreeMonths),
    to: endOfWeek(endLastMonth)
  },
  {
    period: 'Last 6 months',
    from: startOfWeek(lastSixMonths),
    to: endOfWeek(endLastMonth)
  },
  {
    period: 'Last year',
    from: startOfWeek(lastYear),
    to: endOfWeek(endLastMonth)
  },
  {
    period: 'Custom period',
    from: startOfWeek(moment()),
    to: endOfWeek(moment())
  }
];

const parkingBanPeriodOpts = [
  {
    period: 'Next week',
    from: moment().toDate(),
    to: nextWeekEnd.toDate()
  },
  {
    period: 'Current week',
    from: startOfWeek(moment()),
    to: endOfWeek(moment())
  },
  {
    period: 'Next month',
    from: moment().toDate(),
    to: nextMonthEnd.toDate()
  },
  {
    period: 'Next 2 months',
    from: moment().toDate(),
    to: nextTwoMonthsEnd.toDate()
  },
  {
    period: 'Next 3 months',
    from: moment().toDate(),
    to: nextThreeMonthsEnd.toDate()
  },
  {
    period: 'Next 6 months',
    from: moment().toDate(),
    to: nextSixMonthsEnd.toDate()
  },
  {
    period: 'Next year',
    from: moment().toDate(),
    to: nextYearEnd.toDate()
  },
  {
    period: 'Custom period',
    from: startOfWeek(moment()),
    to: endOfWeek(moment())
  }
];

const guestCardPeriodOpts = [
  {
    period: 'Last week',
    from: startOfWeek(lastWeek),
    to: endOfWeek(lastWeek)
  },
  {
    period: 'Current week',
    from: startOfWeek(moment()),
    to: endOfWeek(moment())
  },
  {
    period: 'Custom period',
    from: startOfWeek(moment()),
    to: endOfWeek(moment())
  }
];

const allOpt = {label: 'All', value: 'all', show: true};

const getFirstLevelOptions = (data, labelKey, valueKey) =>
  data.reduce((acc, datum, index) => {
    if (index === 0) {
      acc.push({...allOpt});
    }

    acc.push({label: datum[labelKey], value: datum[valueKey], show: true});

    return acc;
  }, []);

const getCityOptions = (data) =>
  data.reduce((acc, {name, cities}, index) => {
    const tranformCities = cities.map((city) => ({
      label: city.name,
      value: city.name,
      country: name,
      buildings: city.buildings,
      show: true
    }));

    if (index === 0) {
      acc.push({...allOpt});
    }

    return [...acc, ...tranformCities];
  }, []);

const getBuildingOptions = (data) =>
  data.reduce((acc, {value, buildings}, index) => {
    if (index === 0) {
      acc.push({...allOpt});
    } else {
      const tranformBuildings = buildings?.map((building) => ({
        label: building,
        value: building,
        city: value,
        show: true
      }));
      acc = [...acc, ...tranformBuildings];
    }

    return acc;
  }, []);

const getDepartnemtOptions = (data) =>
  data.reduce((acc, {id, departments}, index) => {
    const tranformDepartments = departments.map((department) => ({
      label: department.name,
      value: department.id,
      businessUnit: id,
      sections: department.sections,
      show: true
    }));

    if (index === 0) {
      acc.push({...allOpt});
    }

    return [...acc, ...tranformDepartments];
  }, []);

const getSectionOptions = (data) =>
  data.reduce((acc, {value, sections}, index) => {
    if (index === 0) {
      acc.push({...allOpt});
    } else {
      const tranformSections = sections?.map((section) => ({
        label: section.name,
        value: section.id,
        department: value,
        show: true
      }));
      acc = [...acc, ...tranformSections];
    }

    return acc;
  }, []);

const capitalizeFirstLetter = (string) =>
  string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();

const getThreeFirstLetters = (string) => string.substring(0, 3);

const shortDay = (string) => getThreeFirstLetters(capitalizeFirstLetter(string));

const lineChartDay = (date) => moment(date, 'YYYY-MM-DD').format("DD MMM 'YY");

const getSelectedItems = (items) => {
  if (items.length > 0 && items[items.length - 1].value === allOpt.value) {
    return [items.find((item) => item.value === allOpt.value)];
  }

  return [...items.filter((item) => item.value !== allOpt.value)];
};
const filtersExcludingAll = (data) =>
  data.map(({value}) => value).filter((value) => value !== allOpt.value);

const validateFilterOpt = (data) => data.length > 1 && data.length < 5;

const validateFilterOpts = (
  comparison,
  countries,
  cities,
  businessUnits,
  departments,
  sections,
  buildings
) => {
  if (comparison === 'COUNTRIES') {
    return validateFilterOpt(countries);
  } else if (comparison === 'CITIES') {
    return validateFilterOpt(cities);
  } else if (comparison === 'BUSINESS_UNITS') {
    return validateFilterOpt(businessUnits);
  } else if (comparison === 'DEPARTMENTS') {
    return validateFilterOpt(departments);
  } else if (comparison === 'SECTIONS') {
    return validateFilterOpt(sections);
  } else if (comparison === 'BUILDINGS') {
    return validateFilterOpt(buildings);
  }
  return true;
};

const legendColors = [
  {color: '#5CBCA9', focus: '#43a390'},
  {color: '#E35F52', focus: '#df473a'},
  {color: '#DD9B2B', focus: '#c58920'},
  {color: '#169368', focus: '#14855d'}
];

const getPieChartData = (data, type) => ({
  avg: (
    data.reduce((acc, item) => {
      return acc + item[type];
    }, 0) / data.length
  ).toFixed(2),
  data: data.map((item) => [
    {name: 'A', value: item[type]},
    {name: 'B', value: 100 - item[type]}
  ])
});

const getBookingsPerDate = (data, bookingType) =>
  data.reduce((acc, datum, index) => {
    const {label} = datum;
    const bookingTypePerDate = datum[bookingType];
    if (index === 0) {
      acc = bookingTypePerDate.map(({date, total}) => ({
        name: lineChartDay(date),
        [label ?? 'item']: total
      }));
    } else {
      bookingTypePerDate.forEach(({date}) => {
        const indexFound = acc.findIndex(({name}) => name === lineChartDay(date));
        acc[indexFound][label] = bookingTypePerDate[indexFound].total;
      });
    }

    return acc;
  }, []);

const requestPayload = (
  {from, to},
  {countries, cities, businessUnits, departments, sections, buildings, comparison}
) => {
  return {
    fromDate: getISODate(from),
    toDate: getISODate(to),
    payload: Object.assign(
      {
        countries: filtersExcludingAll(countries),
        cities: filtersExcludingAll(cities),
        businessUnits: filtersExcludingAll(businessUnits),
        departments: filtersExcludingAll(departments),
        sections: filtersExcludingAll(sections),
        buildings: filtersExcludingAll(buildings)
      },
      comparison ? {comparison: comparison} : null
    )
  };
};

const tranformResponse = ({
  totalDeskBookings,
  deskBookingsPerFacilities,
  deskBookingsPerBuildings,
  distributionsAndCancellationRate
}) => {
  return {
    totalDeskBookings,
    occupancyPerFacilities: deskBookingsPerFacilities,
    occupancyPerBuildings: deskBookingsPerBuildings,
    dailyDistribution: distributionsAndCancellationRate.reduce(
      (acc, {dailyDistribution, label}, index) => {
        if (index === 0) {
          acc = dailyDistribution.map(({day, total}) => ({
            name: shortDay(day),
            [label ?? 'item']: total
          }));
        } else {
          dailyDistribution.forEach(({day}) => {
            const indexFound = acc.findIndex(({name}) => name === shortDay(day));
            acc[indexFound][label] = dailyDistribution[indexFound].total;
          });
        }

        return acc;
      },
      []
    ),
    cancellationRate: getPieChartData(distributionsAndCancellationRate, 'cancellationRate'),
    legends: distributionsAndCancellationRate.map(({label}, index) => ({
      title: label ?? 'item',
      ...legendColors[index]
    })),
    deskBookingsPerDate: getBookingsPerDate(
      distributionsAndCancellationRate,
      'deskBookingsPerDate'
    ),
    parkingBookingsPerDate: getBookingsPerDate(
      distributionsAndCancellationRate,
      'parkingBookingsPerDate'
    )
  };
};

const occupancyRateOpts = ['Floors', 'Buildings'];
const bookingsDayOpts = ['Desks', 'Parkings'];

export {
  periodOpts,
  parkingBanPeriodOpts,
  guestCardPeriodOpts,
  getFirstLevelOptions,
  getCityOptions,
  getBuildingOptions,
  getDepartnemtOptions,
  getSectionOptions,
  allOpt,
  shortDay,
  lineChartDay,
  getPieChartData,
  getSelectedItems,
  filtersExcludingAll,
  validateFilterOpts,
  requestPayload,
  tranformResponse,
  occupancyRateOpts,
  bookingsDayOpts,
  startOfWeek,
  endOfWeek,
  isBetweenWeek
};
